import { useCallback } from 'react';
import Vec2 from 'vec2';

import { FAUX_SHAPE_INDEX } from '~/core/constants';
import { drawingTool } from '~/core/engine/DrawingTool';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import {
  isCookingApplianceProduct,
  updateShapeByTranslation,
} from '~/core/helpers';
import { ApplianceType, DrawingAppliance, DrawingShape } from '~/core/models';
import { ApplianceQtyType } from '~/core/types/appliances';
import { cloneDrawingShape, isRange } from '~/core/utils';
import {
  useDrawingShapeContext,
  usePricedProductsContext,
  useUndoStackContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  resetSelectedBrowsingOptionAndCookingApplianceId,
  resetSelectedCookingAndRanges,
  syncCookingApplianceOnShape,
} from '~/redux/slices/cookingAppliance';
import { resetSelectedCornerRadius } from '~/redux/slices/cornerRadii';
import {
  decreaseCustomCutouts,
  selectCustomCutouts,
  setApplianceCutouts,
  setCustomCutouts,
} from '~/redux/slices/cutout';
import { resetSelectedDiagonalCorner } from '~/redux/slices/diagonalCorner';
import { updateNeedRedrawCanvas } from '~/redux/slices/drawingShape';
import { resetSelectedEdge } from '~/redux/slices/edge';
import { selectFabricCanvas } from '~/redux/slices/fabric';
import {
  clearSelectedWarrantyProduct,
  resetCustomLaborOptionalItems,
  resetOptionalItems,
} from '~/redux/slices/optionalItems';
import {
  resetSelectedPredefinedShape,
  updateSelectedDrawingTab,
} from '~/redux/slices/shape';
import {
  resetAllSinkQuantities,
  resetSelectedSink,
  resetSelectedBrowsingOption as resetSelectedSinkBrowsingOption,
} from '~/redux/slices/sink';
import { resetSelectedTearOuts } from '~/redux/slices/tearOut';

export const useToolbarHelper = () => {
  const dispatch = useAppDispatch();
  const canvas = useAppSelector(selectFabricCanvas);

  const {
    getters: {
      drawingShapes,
      activeDrawingShape,
      areThereShapesInTheDrawingArea,
    },
    handlers: { updateActiveDrawingShapeId, updateDrawingShapes },
  } = useDrawingShapeContext();

  const { clearUndoStack } = useUndoStackContext();

  const { resetAllPricing } = usePricedProductsContext();

  const customCutouts = useAppSelector(selectCustomCutouts).selected;

  const duplicateShape = useCallback(
    (shapeIndex: number) => {
      if (!areThereShapesInTheDrawingArea) {
        return;
      }

      const shape = drawingShapes[shapeIndex];
      const duplicatedShape = cloneDrawingShape(shape, true, false);

      // Calculate translation for the new shape position using proper Vec2
      const destVector = new Vec2(50, 50); // Simple offset for now

      updateShapeByTranslation(destVector, duplicatedShape);

      const updatedDrawingShapes = [...drawingShapes, duplicatedShape];

      updateDrawingShapes(updatedDrawingShapes);

      duplicatedShape.appliances.forEach((appliance: DrawingAppliance) => {
        const { productId, applianceType } = appliance.applianceModel;

        if (isCookingApplianceProduct(appliance.applianceModel)) {
          const selectedType = isRange({ applianceType })
            ? 'selectedRangeApplianceProduct'
            : 'selectedCooktopApplianceProduct';

          dispatch(
            syncCookingApplianceOnShape({
              selectedType,
              applianceId: productId,
              count: 1,
            })
          );
        }
      });

      updateActiveDrawingShapeId(duplicatedShape.id);
      dispatch(updateNeedRedrawCanvas(true));
    },
    [
      areThereShapesInTheDrawingArea,
      dispatch,
      drawingShapes,
      updateActiveDrawingShapeId,
      updateDrawingShapes,
    ]
  );

  const resetSelectedCardsOptions = useCallback(() => {
    dispatch(resetSelectedEdge());
    dispatch(resetSelectedCornerRadius());
    dispatch(resetAllSinkQuantities());
    dispatch(resetSelectedTearOuts());
    dispatch(resetOptionalItems());
    dispatch(resetCustomLaborOptionalItems());
    dispatch(clearSelectedWarrantyProduct());
    dispatch(setCustomCutouts());
    dispatch(setApplianceCutouts());
    dispatch(resetSelectedCookingAndRanges());
  }, [dispatch]);

  const cleanupCutoutsOnActiveShape = useCallback(() => {
    // Clean up cutouts associated with appliances on the active shape
    activeDrawingShape?.appliances.forEach((appliance) => {
      const { productId, applianceType } = appliance.applianceModel;

      // Handle cutouts that are directly appliances (like electrical cutouts)
      const cutoutsOnShape = [
        ApplianceType.ELECTRICAL_CUTOUT,
        ApplianceType.FAUCET,
        ApplianceType.FAUCET_CUTOUT,
      ];

      if (cutoutsOnShape.includes(applianceType)) {
        const currentCutout = customCutouts[productId];

        if (currentCutout?.onShape > 0) {
          dispatch(
            decreaseCustomCutouts({
              cutoutId: productId,
              qtyType: ApplianceQtyType.onShape,
            })
          );
        }
      }
    });
  }, [customCutouts, dispatch]);

  const removeActiveShape = useCallback(
    (updatedDrawingShapes: DrawingShape[] = []) => {
      if (
        !activeDrawingShape ||
        updatedDrawingShapes.length === drawingShapes.length
      ) {
        return false;
      }

      activeDrawingShape?.onRename?.unsubscribe();
      dispatch(resetSelectedDiagonalCorner());
      dispatch(resetSelectedBrowsingOptionAndCookingApplianceId());
      dispatch(resetSelectedSink());
      dispatch(resetSelectedSinkBrowsingOption());

      if (updatedDrawingShapes.length > 1) {
        updateActiveDrawingShapeId(updatedDrawingShapes[1].id);
        updateDrawingShapes([...updatedDrawingShapes]);
      } else {
        // remove all shapes and everything else
        resetSelectedCardsOptions();
        drawingShapes[FAUX_SHAPE_INDEX].appliances = [];
        drawingShapes.splice(1, drawingShapes.length - 1);
        updateDrawingShapes([...drawingShapes]);
        clearUndoStack();
        dispatch(resetSelectedPredefinedShape());
        dispatch(
          updateSelectedDrawingTab(DrawingTabEnum.MODIFY_PREDEFINED_SHAPE_TAB)
        );
        updateActiveDrawingShapeId(undefined);
        resetAllPricing(); // resets area qty to 0
        drawingTool.clearCanvas({ canvas });
      }
      dispatch(updateNeedRedrawCanvas(true));

      return true;
    },
    [
      activeDrawingShape,
      drawingShapes,
      cleanupCutoutsOnActiveShape,
      dispatch,
      updateActiveDrawingShapeId,
      updateDrawingShapes,
    ]
  );

  return {
    duplicateShape,
    removeActiveShape,
    cleanupCutoutsOnActiveShape,
  };
};
